<template>
  <d-modal
    :active="isActive"
    :width="600"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-2xl font-bold">
          {{ subjectArea ? 'Edit Subject Area' : 'Add New Subject Area' }}
        </h3>
        <p>
          {{ subjectArea ? 'Update subject area information' : 'Create a new subject area' }}
        </p>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="space-y-4">
        <!-- Name Field -->
        <d-field label="Name *">
          <d-input 
            v-model="form.name" 
            :required="true"
            placeholder="Enter subject area name"
          />
        </d-field>

        <!-- Description Field -->
        <d-field label="Description *">
          <d-input 
            v-model="form.description" 
            :required="true"
            placeholder="Enter description"
          />
        </d-field>

        <!-- Image Upload -->
        <d-field label="Image">
          <div class="flex items-center gap-4">
            <div 
              class="bg-no-repeat bg-cover bg-center h-32 w-32 shadow rounded border"
              :style="{'background-image': `url('${imagePreview}')`}"
            />
            <div>
              <input 
                ref="imageUpload" 
                type="file" 
                accept="image/*"
                class="hidden" 
                @change="onSelectFile"
              >
              <d-btn 
                color="secondary"
                @click="$refs.imageUpload.click()"
              >
                Browse
              </d-btn>
              <p class="text-sm mt-2">
                Select an image file
              </p>
            </div>
          </div>
        </d-field>

        <!-- Order Field -->
        <d-field label="Order">
          <d-input 
            v-model.number="form.order" 
            type="number"
            min="1"
            placeholder="Display order"
          />
        </d-field>

        <!-- Manual Ordering Toggle -->
        <d-field label="Resource Sort Order">
          <div class="flex items-center gap-3 mt-1">
            <input
              id="useManualOrdering"
              v-model="form.useManualOrdering"
              type="checkbox"
              class="checkbox"
            />
            <label for="useManualOrdering" class="cursor-pointer text-sm">
              Use manual ordering (uncheck to default to newest-first sorting for users)
            </label>
          </div>
        </d-field>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center py-4">
          <span class="loading loading-spinner loading-md"></span>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end gap-3 pt-4">
          <d-btn 
            type="button"
            flat 
            @click="handleClose"
          >
            Cancel
          </d-btn>
          <d-btn 
            type="submit"
            color="primary"
            :disabled="!isFormValid || loading"
          >
            {{ subjectArea ? 'Update' : 'Create' }} Subject Area
          </d-btn>
        </div>
      </form>
    </div>
  </d-modal>
</template>

<script>
import axios from 'axios';
import { toast } from '@services/toast.service';

export default {
  name: 'SubjectAreaModal',
  
  props: {
    active: {
      type: Boolean,
      default: false
    },
    subjectArea: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      form: {
        name: '',
        description: '',
        order: 1,
        imageFile: null
      },
      imagePreview: ''
    };
  },

  computed: {
    isActive() {
      return this.active;
    },
    
    isFormValid() {
      return this.form.name && this.form.description;
    }
  },

  watch: {
    active(newVal) {
      if (newVal) {
        this.resetForm();
        if (this.subjectArea) {
          this.populateForm();
        }
      }
    }
  },

  methods: {
    resetForm() {
      this.form = {
        name: '',
        description: '',
        order: 1,
        useManualOrdering: false,
        imageFile: null
      };
      this.imagePreview = '';
    },

    populateForm() {
      if (this.subjectArea) {
        this.form.name = this.subjectArea.name || '';
        this.form.description = this.subjectArea.description || '';
        this.form.order = this.subjectArea.order || 1;
        this.form.useManualOrdering = this.subjectArea.useManualOrdering || false;
        this.imagePreview = this.subjectArea.imageUrl || '';
      }
    },

    onSelectFile(event) {
      const file = event.target.files[0];
      if (file) {
        this.form.imageFile = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    async uploadImage(subjectAreaId) {
      if (!this.form.imageFile) return null;

      const formData = new FormData();
      formData.append('image', this.form.imageFile);

      const response = await axios.put(`/api/subject-areas/${subjectAreaId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return response.data;
    },

    async handleSubmit() {
      if (!this.isFormValid) return;

      this.loading = true;
      try {
        // Prepare JSON data for subject area
        const subjectAreaData = {
          name: this.form.name,
          description: this.form.description,
          order: this.form.order,
          useManualOrdering: this.form.useManualOrdering
        };

        if (this.subjectArea) {
          // Update existing subject area
          await axios.patch(`/api/subject-areas/${this.subjectArea.id}`, subjectAreaData, {
            headers: { 'Content-Type': 'application/json' }
          });

          // Upload image if a new one was selected
          if (this.form.imageFile) {
            await this.uploadImage(this.subjectArea.id);
          }

          toast.success('Subject area updated successfully');
        } else {
          // Create new subject area
          const createResponse = await axios.post('/api/subject-areas/items', subjectAreaData, {
            headers: { 'Content-Type': 'application/json' }
          });

          // Upload image if one was selected
          if (this.form.imageFile && createResponse.data?.id) {
            await this.uploadImage(createResponse.data.id);
          }

          toast.success('Subject area created successfully');
        }

        this.$emit('saved');
      } catch (error) {
        console.error('Error saving subject area:', error);
        
        // Handle validation errors from the API
        if (error.response?.data?.errors) {
          const errorMessages = Array.isArray(error.response.data.errors) 
            ? error.response.data.errors.join(', ')
            : Object.values(error.response.data.errors).flat().join(', ');
          toast.error(`Failed to save subject area: ${errorMessages}`);
        } else if (error.response?.data?.message) {
          toast.error(`Failed to save subject area: ${error.response.data.message}`);
        } else {
          toast.error('Failed to save subject area');
        }
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Remove duplicate styling to fix ghost outline issue */
</style>
